'use client'

import { useAbTest } from 'ab-test/hooks/useAbTest/useAbTest'
import { ViewElement } from 'analytics/components/ViewElement/ViewElement'
import {
	FormFieldIds,
	FormFieldNames,
	SUBMIT_BUTTON_ID,
} from 'back-in-stock/constants'
import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { FormProvider, useForm } from 'form-components'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { RegistryCheckbox as NewsletterCheckbox } from 'newsletter/components/RegistryCheckbox/RegistryCheckbox'
import { NewsletterAnalytics } from 'newsletter/constants'
import { useEffect } from 'react'
import { useSession } from 'session/src/hooks/useSession'
import { AbTestKeys } from 'types/constants/AbTestKeys'
import { useUserDetails } from 'user-data/hooks/useUserDetails'
import { useBackInStock } from '../../../hooks/useBackInStock/useBackInStock'
import type { FormValues } from '../../../types/form'
import styles from './BackInStockForm.module.scss'
import { GenericError } from './GenericError/GenericError'

export function BackInStockForm(): React.ReactNode {
	const { pageType } = useMasterData()
	const { t } = useLabels()
	const { logged, email } = useSession()

	const userDetails = useUserDetails(true, true)
	const isUserContactable = !!(
		logged && userDetails?.contactability.isContactable
	)

	const newsletterAbTest = useAbTest({
		key: AbTestKeys.BackInStockNewsletter,
		defaultDecision: { enabled: false },
		preventRequestSDK: isUserContactable,
	})
	const isNewsletterEnabled = !!newsletterAbTest?.enabled
	const { subscribeProduct, subscribeError } =
		useBackInStock(isNewsletterEnabled)

	const defaultEmail = logged ? email : ''

	const formMethods = useForm({
		defaultValues: {
			[FormFieldNames.Email]: defaultEmail,
			[FormFieldNames.Newsletter]: false,
		},
		mode: 'onTouched',
	})

	const fieldValidations = useFieldValidations()

	const onSubmit = async (values: FormValues): Promise<void> => {
		await subscribeProduct(values)
	}

	const { handleSubmit, setError } = formMethods

	useEffect(() => {
		if (subscribeError) {
			setError('root', {})
		}
	}, [subscribeError])

	return (
		<FormProvider {...formMethods}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<EmailInput
					name={FormFieldNames.Email}
					id={FormFieldIds.Email}
					rules={fieldValidations.email}
					data-testid={`${pageType}.${FormFieldIds.Email}`}
				/>
				{isNewsletterEnabled && !isUserContactable && (
					<ViewElement eventName={NewsletterAnalytics.NEWSLETTER_SHOW}>
						<NewsletterCheckbox
							id={FormFieldIds.Newsletter}
							name={FormFieldNames.Newsletter}
						/>
					</ViewElement>
				)}
				<GenericError />
				<SubmitButton
					id={SUBMIT_BUTTON_ID}
					data-testid={`${pageType}.${SUBMIT_BUTTON_ID}`}
				>
					{t('notifyMe.modal.button')}
				</SubmitButton>
			</form>
		</FormProvider>
	)
}
